import { Badge } from '@/components/ui/badge'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Attendant } from '@/interfaces/attendant'
import { Department } from '@/interfaces/department'
import { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from './data-table-column-header'
import { DataTableRowActions } from './data-table-row-actions'

export const columns: ColumnDef<Attendant>[] = [
  {
    id: 'user.name',
    accessorKey: 'user.name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome" />
    ),
    filterFn: (row, id, value) => {
      return row.getValue(id)?.toString().toLowerCase().includes(value.toLowerCase()) ?? false
    },
  },
  {
    accessorKey: 'user.email',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
  },
  {
    accessorKey: 'user.phone',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Telefone" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'departments',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Departamentos' />
    ),
    cell: ({ row }) => {
      const departments = row.getValue<Department[]>('departments')

      if (!departments) {
        return null
      }

      return (
        <div className='flex flex-wrap gap-2 items-center'>
          <span className='text-muted-foreground'>{departments[0]?.name}</span>
          {departments.length > 1 && (
            <HoverCard>
              <HoverCardTrigger>
                <Badge variant='outline' className='text-muted-foreground'>+ {departments.length - 1}</Badge>
              </HoverCardTrigger>
              <HoverCardContent>
                <ul className='list-none p-0 m-0'>
                  {departments.slice(1).map((segment) => (
                    <li key={segment.id}>{segment.name}</li>
                  ))}
                </ul>
              </HoverCardContent>
            </HoverCard>
          )}
        </div>
      )
    },
    filterFn: (row, id, value) => {
      const departments = row.getValue<Department[]>('departments')
      return departments?.some((department) => value.includes(department.id.toString())) ?? false
    },
    enableSorting: false,
  },
  {
    accessorKey: 'role',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Cargo" />
    ),
    cell: ({ row }) => {
      const role = row.getValue('role') as string
      return (
        <div className="flex items-center">
          <span>{role === 'MANAGER' ? 'Gerente' : 'Atendente'}</span>
        </div>
      )
    },
    filterFn: (row, id, value) => {
      return row.getValue(id) === value
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const status = row.getValue('status') as string
      return (
        <div className="flex items-center">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
            status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
            {status === 'ACTIVE' ? 'Ativo' :
              status === 'PENDING' ? 'Pendente' :
                'Desativado'}
          </span>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    id: 'actions',
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
]
